import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  List,
  ListItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import GrowthChart from "../charts/GrowthChart";
import {
  formatTimeNoSeconds,
  formatDay,
  groupLogByDay,
} from "./trackerHelpers";

const WeightTab = ({
  weightLog,
  onAddWeight,
  onEditEntry,
  currentChild,
  getChildAgeAtEntry,
}) => {
  const { t } = useTranslation();
  const [showWeightTable, setShowWeightTable] = useState(false);
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [parentWeight, setParentWeight] = useState("");
  const [parentPlusBabyWeight, setParentPlusBabyWeight] = useState("");
  const [weightValue, setWeightValue] = useState("");

  const handleParentWeightChange = (value) => {
    setParentWeight(value);
    if (value !== "" && parentPlusBabyWeight !== "") {
      setWeightValue(parentPlusBabyWeight - value);
    }
  };

  const handleCombinedWeightChange = (value) => {
    setParentPlusBabyWeight(value);
    if (value !== "" && parentWeight !== "") {
      setWeightValue(value - parentWeight);
    }
  };

  const handleSave = async () => {
    await onAddWeight(weightValue);
    setWeightValue("");
    setParentWeight("");
    setParentPlusBabyWeight("");
    setIsWeightModalOpen(false);
  };

  const groupedWeight = groupLogByDay(weightLog);
  const sortedWeightDays = Object.keys(groupedWeight).sort(
    (a, b) => new Date(b) - new Date(a),
  );

  return (
    <Box sx={{ mt: 2 }}>
      {sortedWeightDays.length === 0 ? (
        <Typography
          variant="body1"
          align="center"
        >
          No weight registrations found.
        </Typography>
      ) : (
        <>
          <GrowthChart weightLog={weightLog} />
          <Paper
            elevation={1}
            sx={{ borderRadius: 2, mt: 2, mb: 2, bgcolor: "background.card" }}
          >
            <ListItem
              secondaryAction={
                <Switch
                  checked={showWeightTable}
                  onChange={(e) => setShowWeightTable(e.target.checked)}
                  color="primary"
                />
              }
            >
              <Typography variant="body2">{t("charts.show_weight_table")}</Typography>
            </ListItem>
          </Paper>
          {showWeightTable && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {sortedWeightDays.map((day) => {
                const dayEntries = groupedWeight[day];
                const sorted = [...dayEntries].sort(
                  (a, b) =>
                    new Date(b.start).getTime() - new Date(a.start).getTime(),
                );
                return (
                  <Paper
                    key={day}
                    elevation={1}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "background.card",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: darken("#8fb9a8", 0.2),
                        color: "white",
                        textAlign: "center",
                        py: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                      >
                        {formatDay(dayEntries[0].start)}
                      </Typography>
                      <Chip
                        label={`⚖️ ${dayEntries.length}`}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <List disablePadding>
                      {sorted.map((entry, index) => (
                        <ListItem
                          key={entry.id}
                          divider={index < sorted.length - 1}
                          onClick={() => onEditEntry(entry)}
                          sx={{
                            cursor: "pointer",
                            flexDirection: "column",
                            alignItems: "stretch",
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTimeNoSeconds(entry.start)}
                            </Typography>
                            {currentChild && (
                              <Chip
                                label={getChildAgeAtEntry(
                                  currentChild.birthDate,
                                  entry.start,
                                )}
                                size="small"
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: entry.background,
                                borderRadius: "50%",
                              }}
                            >
                              {entry.icon}
                            </Box>
                            <Chip
                              label={`${entry.weight} g`}
                              size="small"
                            />
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                );
              })}
            </Box>
          )}
        </>
      )}

      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setIsWeightModalOpen(true)}
        sx={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: darken("#7AA490", 0.2),
          "&:hover": { backgroundColor: darken("#7AA490", 0.3) },
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog
        open={isWeightModalOpen}
        onClose={() => setIsWeightModalOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>Register Weight</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              type="number"
              label="Parent Weight (g)"
              InputLabelProps={{ shrink: true }}
              value={parentWeight}
              onChange={(e) =>
                handleParentWeightChange(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              fullWidth
            />
            <TextField
              type="number"
              label="Parent + Baby Weight (g)"
              InputLabelProps={{ shrink: true }}
              value={parentPlusBabyWeight}
              onChange={(e) =>
                handleCombinedWeightChange(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              fullWidth
            />
            <TextField
              type="number"
              label="Baby Weight (g)"
              InputLabelProps={{ shrink: true }}
              value={weightValue}
              onChange={(e) => setWeightValue(Number(e.target.value))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!weightValue || weightValue <= 0}
            sx={{
              backgroundColor: darken("#fefad4", 0.2),
              "&:hover": { backgroundColor: darken("#fefad4", 0.3) },
            }}
          >
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeightTab;
